/**
 * Slot type label helper for the client space.
 *
 * Bookings store `slot_type` as a code (e.g. "full_day", "half_day", "matin",
 * "apres-midi", "journee", "journee_2_personnes") and an optional
 * `slot_period` ("morning" / "afternoon") for ambiguous half-day codes.
 *
 * This helper returns a normalised description so the UI can render a
 * consistent badge or label without repeating heuristics across pages.
 */
export const getSlotTypeInfo = (booking = {}) => {
  const rawCode = (booking.slot_type || '').toString();
  const code = rawCode.toLowerCase();
  const period = booking.slot_period;

  // 2-person full-day variants — handle before generic full-day match.
  if (/2[\s_-]*personnes?|2pers|two_person/i.test(code)) {
    return {
      label: 'Journée 2 personnes',
      short: 'Journée ×2',
      hours: 7,
      timeRange: '8h - 18h',
      kind: 'full_multi',
    };
  }

  // Full day variants
  if (code === 'full_day' || /journ[eé]e|complete|full/.test(code)) {
    return {
      label: 'Journée complète',
      short: 'Journée',
      hours: 7,
      timeRange: '8h - 18h',
      kind: 'full',
    };
  }

  // Morning-specific slot types
  if (code === 'matin' || /^matin\b/.test(code)) {
    return {
      label: 'Matin',
      short: 'Matin',
      hours: 4,
      timeRange: '8h - 12h',
      kind: 'morning',
    };
  }

  // Afternoon-specific slot types
  if (/apres|aprem/.test(code)) {
    return {
      label: 'Après-midi',
      short: 'Après-midi',
      hours: 4,
      timeRange: '14h - 18h',
      kind: 'afternoon',
    };
  }

  // Generic half_day — disambiguate via slot_period
  if (code === 'half_day') {
    if (period === 'morning') {
      return {
        label: 'Demi-journée — Matin',
        short: 'Matin',
        hours: 4,
        timeRange: '8h - 12h',
        kind: 'morning',
      };
    }
    if (period === 'afternoon') {
      return {
        label: 'Demi-journée — Après-midi',
        short: 'Après-midi',
        hours: 4,
        timeRange: '14h - 18h',
        kind: 'afternoon',
      };
    }
    return {
      label: 'Demi-journée',
      short: 'Demi-journée',
      hours: 4,
      timeRange: '8h - 12h',
      kind: 'half',
    };
  }

  // Unknown — return raw code so devs can spot anomalies in production.
  return {
    label: rawCode || '—',
    short: rawCode || '—',
    hours: null,
    timeRange: '',
    kind: 'unknown',
  };
};

/**
 * Tailwind color classes per kind — keeps the badge palette consistent
 * across the client UI (subscription RDV list, scheduling modal, etc.).
 */
export const slotTypeBadgeClasses = {
  full: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  full_multi: 'bg-purple-100 text-purple-800 border-purple-200',
  morning: 'bg-amber-100 text-amber-800 border-amber-200',
  afternoon: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  half: 'bg-slate-100 text-slate-800 border-slate-200',
  unknown: 'bg-slate-100 text-slate-700 border-slate-200',
};

/** Small emoji per kind to add quick visual recognition. */
export const slotTypeEmoji = {
  full: '📅',
  full_multi: '👥',
  morning: '🌅',
  afternoon: '🌇',
  half: '🕐',
  unknown: '•',
};
